import { bannerErrorFragment } from "@saleor/fragments/errors";
import { pageInfoFragment } from "@saleor/fragments/pageInfo";
import makeMutation from "@saleor/hooks/makeMutation";
import gql from "graphql-tag";

import { TypedMutation } from "../mutations";
import {
  BannerBulkDelete,
  BannerBulkDeleteVariables
} from "./types/BannerBulkDelete";
import { BannerCreate, BannerCreateVariables } from "./types/BannerCreate";
import {
  BannerCreateDeeplink,
  BannerCreateDeeplinkVariables
} from "./types/BannerCreateDeeplink";
import { BannerDelete, BannerDeleteVariables } from "./types/BannerDelete";
import {
  BannerImageCreate,
  BannerImageCreateVariables
} from "./types/BannerImageCreate";
import {
  BannerLinkUpdate,
  BannerLinkUpdateVariables
} from "./types/BannerLinkUpdate";
import {
  BannerMallsAdd,
  BannerMallsAddVariables
} from "./types/BannerMallsAdd";
import {
  BannerMallsRemove,
  BannerMallsRemoveVariables
} from "./types/BannerMallsRemove";
import { BannerUpdate, BannerUpdateVariables } from "./types/BannerUpdate";

const bannerCreate = gql`
  ${bannerErrorFragment}
  mutation BannerCreate($input: BannerCreateInput!) {
    bannerCreate(input: $input) {
      errors: bannerErrors {
        ...BannerErrorFragment
      }
      banner {
        id
        name
      }
    }
  }
`;
export const TypedBannerCreate = TypedMutation<
  BannerCreate,
  BannerCreateVariables
>(bannerCreate);

const bannerUpdate = gql`
  ${bannerErrorFragment}
  mutation BannerUpdate($id: ID!, $input: BannerUpdateInput!) {
    bannerUpdate(id: $id, input: $input) {
      errors: bannerErrors {
        ...BannerErrorFragment
      }
      banner {
        id
        name
      }
    }
  }
`;
export const TypedBannerUpdate = TypedMutation<
  BannerUpdate,
  BannerUpdateVariables
>(bannerUpdate);

export const bannerImageCreateMutation = gql`
  mutation BannerImageCreate($banner: ID!, $image: Upload!) {
    bannerImageCreate(input: { image: $image, banner: $banner }) {
      errors: bannerErrors {
        field
        message
        code
      }
      banner {
        id
        name
        imageUrl {
          url
        }
      }
    }
  }
`;
export const useBannerImageCreateMutation = makeMutation<
  BannerImageCreate,
  BannerImageCreateVariables
>(bannerImageCreateMutation);

const bannerDelete = gql`
  mutation BannerDelete($id: ID!) {
    bannerDelete(id: $id) {
      errors: bannerErrors {
        field
        message
      }
    }
  }
`;
export const TypedBannerDelete = TypedMutation<
  BannerDelete,
  BannerDeleteVariables
>(bannerDelete);

const bannerBulkDelete = gql`
  mutation BannerBulkDelete($ids: [ID]!) {
    bannerBulkDelete(ids: $ids) {
      errors {
        field
        message
      }
    }
  }
`;
export const TypedBannerBulkDelete = TypedMutation<
  BannerBulkDelete,
  BannerBulkDeleteVariables
>(bannerBulkDelete);

const bannerMallsAdd = gql`
  ${pageInfoFragment}
  mutation BannerMallsAdd(
    $input: BannerMallsInput!
    $id: ID!
    $after: String
    $before: String
    $first: Int
    $last: Int
  ) {
    bannerAddMalls(id: $id, input: $input) {
      bannerErrors {
        message
        code
      }
      banner {
        id
        malls(after: $after, before: $before, first: $first, last: $last) {
          edges {
            node {
              id
              name
            }
          }
          totalCount
          pageInfo {
            ...PageInfoFragment
          }
        }
      }
    }
  }
`;
export const TypedBannerMallsAdd = TypedMutation<
  BannerMallsAdd,
  BannerMallsAddVariables
>(bannerMallsAdd);

const bannerMallsRemove = gql`
  ${pageInfoFragment}
  mutation BannerMallsRemove(
    $input: BannerMallsInput!
    $id: ID!
    $after: String
    $before: String
    $first: Int
    $last: Int
  ) {
    bannerRemoveMalls(id: $id, input: $input) {
      bannerErrors {
        message
        code
      }
      banner {
        id
        malls(after: $after, before: $before, first: $first, last: $last) {
          edges {
            node {
              id
              name
            }
          }
          totalCount
          pageInfo {
            ...PageInfoFragment
          }
        }
        product {
          id
          name
          productType {
            id
            name
          }
        }
        collection {
          id
          name
          isPublished
          products {
            totalCount
          }
        }
        brand {
          id
          name
          legalName
        }
      }
    }
  }
`;
export const TypedBannerMallsRemove = TypedMutation<
  BannerMallsRemove,
  BannerMallsRemoveVariables
>(bannerMallsRemove);

const bannerLinkUpdate = gql`
  mutation BannerLinkUpdate($id: ID!, $input: BannerAnnouncementLinkInput!) {
    bannerAnnouncementLinkUpdate(id: $id, input: $input) {
      errors: bannerErrors {
        field
        message
        code
      }
      banner {
        id
        product {
          id
          name
          productType {
            id
            name
          }
        }
        collection {
          id
          name
          isPublished
          products {
            totalCount
          }
        }
        brand {
          id
          name
          legalName
        }
        category {
          id
          name
          level
          products {
            totalCount
          }
        }
        coupon {
          id
          name
          code
          redeemType
          startDate
          endDate
          status
        }
        couponCollection {
          id
          title
          classification
          collectionType
          status
          startDate
          endDate
        }
      }
    }
  }
`;
export const TypedBannerLinkUpdate = TypedMutation<
  BannerLinkUpdate,
  BannerLinkUpdateVariables
>(bannerLinkUpdate);

const bannerCreateDeeplink = gql`
  mutation BannerCreateDeeplink($id: ID!) {
    bannerGenerateDynamicLink(id: $id) {
      bannerErrors {
        field
        code
        message
      }
      banner {
        id
        deeplinkUrl
        shortDynamicLink
      }
    }
  }
`;
export const useBannerCreateDeeplink = makeMutation<
  BannerCreateDeeplink,
  BannerCreateDeeplinkVariables
>(bannerCreateDeeplink);
